import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { TabsContainerProps } from './Tabs.types'
import { BrandIcon } from '../../Compounds/src/BrandIconCompound'
import { sanitizeHTML } from '../../../utils/common-utils'

export type ChildRef = {
  focusTab: (index: number) => void
}

const TabsContainer = forwardRef<ChildRef, TabsContainerProps>(
  ({ tabs, activeTab, setActiveTab, testId, mobileButtonRefs, buttonsRef, isCommitment }, ref) => {
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

    const handleTabClick = (index: number) => {
      setActiveTab(tabs[index])
      tabRefs.current[index]!.click()
      tabRefs.current[index]!.focus()
      tabRefs.current[index]!.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }

    const handleTabKeydown = (
      event: React.KeyboardEvent<HTMLButtonElement>,
      index: number
    ): void => {
      if (event.key === 'ArrowRight' && index < tabs?.length - 1) {
        handleTabClick(index + 1)
      } else if (event.key === 'ArrowLeft' && index > 0) {
        handleTabClick(index - 1)
      } else if (
        event.key === 'Tab' &&
        buttonsRef.current[index] &&
        mobileButtonRefs.current[index]
      ) {
        event.preventDefault()
        buttonsRef.current[index]!.focus()
        mobileButtonRefs.current[index]!.focus()
      } else if (event.key === 'Tab' && index < tabs?.length - 1) {
        event.preventDefault()
        handleTabClick(index + 1)
      }
    }

    useImperativeHandle(ref, () => ({
      focusTab: (index: number) => {
        tabRefs.current[index]!.click()
        tabRefs.current[index]!.focus()
        tabRefs.current[index]!.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      },
    }))

    return (
      <div className='u-sizeConstrained Grid u-marginCenter'>
        <div
          className={`Tabs Content u-flex u-flexRow u-lg-flexJustifyCenter u-md-flexJustifyCenter u-overflowX`}>
          {tabs?.map((tab, index) => (
            <div className='u-paddingHoriz2gu navItem' key={`${tab.identifier}-${index}`}>
              <button
                id={tab.buttonId}
                role='tab'
                aria-controls={`${tab.identifier}-${index}`}
                tabIndex={0}
                className={`tab u-sm-marginTop1gu u-paddingBottom4gu u-paddingTop3gu ${
                  isCommitment ? 'CommitmentsTab' : ''
                } ${activeTab?.identifier === tab.identifier ? 'activeItem' : ''}`}
                aria-selected={activeTab?.identifier === tab.identifier}
                data-analytics={tab.analytics}
                onClick={() => {
                  handleTabClick(index)
                }}
                onKeyDown={event => {
                  handleTabKeydown(event, index)
                }}
                ref={el => {
                  tabRefs.current[index] = el
                }}>
                {tab.icon && (
                  <BrandIcon
                    iconsDropdown={tab.icon!.iconsDropdown}
                    alignment={tab.icon!.alignment!?.length > 0 ? tab.icon!.alignment : 'center'}
                    enableBorder={false}
                    size={tab.icon!.size!?.length > 0 ? tab.icon!.size : 'u-sizeDefault'}
                    identifier={tab.identifier}
                    testId={testId ? `${testId}-${index}` : undefined}
                  />
                )}
                <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(tab.title ?? '') }}></span>
              </button>
            </div>
          ))}
        </div>
      </div>
    )
  }
)

export default TabsContainer
